import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";
import { ToastContainer, toast } from "react-toastify";
import { Lock } from "lucide-react";
import {
  createOrUpdateTreatment,
  CreateUpdateTreatmentPayload,
  getTreatmentById,
  getAllMedicalConditions,
  getAllAllergies,
  getAllHabits,
  deletePreTreatmentPhoto,
} from "../../api/treatment";
import { getAllPatients } from "../../api/patient";
import { getClinicsByUser } from "../../api/clinic";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ConfirmModal from "../../components/ConfirmModal";

// Dental examination type options
const DENTAL_EXAMINATION_OPTIONS = [
  { label: "Child", value: "child" },
  { label: "Adult", value: "adult" },
];

// Child teeth options
const CHILD_UPPER_ROW1 = [51, 52, 53, 54, 55];
const CHILD_UPPER_ROW2 = [61, 62, 63, 64, 65];
const CHILD_LOWER_ROW1 = [71, 72, 73, 74, 75];
const CHILD_LOWER_ROW2 = [81, 82, 83, 84, 85];

// Adult teeth options
const ADULT_UPPER_ROW1 = [11, 12, 13, 14, 15, 16, 17, 18];
const ADULT_UPPER_ROW2 = [21, 22, 23, 24, 25, 26, 27, 28];
const ADULT_LOWER_ROW1 = [31, 32, 33, 34, 35, 36, 37, 38];
const ADULT_LOWER_ROW2 = [41, 42, 43, 44, 45, 46, 47, 48];

const diagnosis_options = [
  "C I – Molar relationship normal",
  "C II – Division 1 – Upper incisors protrude",
  "C II – Division 2 – Upper incisors retrude",
  "C II – Subdivision – Unilateral (one side) Class II relationship",
  "C III – Subdivision – Unilateral (one side) Class III relationship",
  "Other"
];


const PatientCreate: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CreateUpdateTreatmentPayload>({
    patientId: "",
    doctorId: localStorage.getItem("userId") || "",
    clinicId: "",
    medicalConditionId: "",
    overjet: "",
    chiefComplaint: "",
    diagnosis: "",
    other_diagnosis: "",
    allergyId: "",
    habit: "",
    other_habit: "",
    facialProfile: "",
    nasolabialAngle: "",
    crossbite: "",
    lipPosture: "",
    noseSize: "",
    treatmentType: "",
    lipProtrusion: "",
    vto: "",
    tonsils: "",
    facialForm: "",
    mentolabialSulcus: "",
    frenum: "",
    oralHygieneStatus: "",
    chin: "",
    facialSymmetry: "",
    facialDivergence: "",
    tongueSize: "",
    treatmentObjective: "",
  });
  const [errors, setErrors] = useState<{
    patientId?: string;
    clinicId?: string;
    facialDivergence?: string;
    tongueSize?: string;
    facialSymmetry?: string;
    chin?: string;
    oralHygieneStatus?: string;
    frenum?: string;
    mentolabialSulcus?: string;
    facialForm?: string;
    tonsils?: string;
    vto?: string;
    lipProtrusion?: string;
    treatmentType?: string;
    noseSize?: string;
    lipPosture?: string;
    crossbite?: string;
    nasolabialAngle?: string;
    facialProfile?: string;
    treatmentObjective?: string;
  }>({});
  const fetchedClinicsRef = useRef(false);
  const [clinicsLoading, setClinicsLoading] = useState(false);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [clinics, setClinics] = useState<{ id: string; name: string }[]>([]);
  const [patients, setPatients] = useState<{
    clinicId: string; id: string; name: string
  }[]>([]);
  const [medicalConditions, setMedicalConditions] = useState<
    { id: string; name: string }[]
  >([]);
  const [allergies, setAllergies] = useState<{ id: string; name: string }[]>(
    []
  );
  const [habits, setHabits] = useState<{ id: string; name: string }[]>([]);
  const [medicalLoading, setMedicalLoading] = useState(false);
  const [allergyLoading, setAllergyLoading] = useState(false);
  const [habitLoading, setHabitLoading] = useState(false);
  const [preTreatmentPhotos, setPreTreatmentPhotos] = useState<File[]>([]);
  const [existingPreTreatmentPhotos, setExistingPreTreatmentPhotos] = useState<
    { id: string; url: string }[]
  >([]);
  const [isPhotosModalOpen, setIsPhotosModalOpen] = useState(false);
  const [showPhotoDeleteConfirm, setShowPhotoDeleteConfirm] = useState(false);
  const [selectedPhotoId, setSelectedPhotoId] = useState<string>("");
  const [selectedMedicalConditionIds, setSelectedMedicalConditionIds] =
    useState<string[]>([]);
  const [isMedicalDropdownOpen, setIsMedicalDropdownOpen] = useState(false);
  const [isPreFilledFromPatient, setIsPreFilledFromPatient] = useState(false);
  const medicalDropdownRef = useRef<HTMLDivElement | null>(null);

  // Dental examination states
  const [dentalExaminationType, setDentalExaminationType] = useState<"child" | "adult">("adult");
  const [missingTeeth, setMissingTeeth] = useState<number[]>([]);
  const [extractedTeeth, setExtractedTeeth] = useState<number[]>([]);
  const [open, setOpen] = useState<boolean>(false);

  const fetchedPatientRef = useRef(false);

  const parseMedicalConditionIds = (
    value?: string | string[] | null
  ): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value
        .map((item) => (typeof item === "string" ? item.trim() : ""))
        .filter((item) => item.length > 0);
    }
    if (typeof value === "string") {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    }
    return [];
  };

  // Cycle function for tooth selection
  // Present (default) -> Missing (1st click) -> Extracted (2nd click) -> Present (3rd click)
  const cycleToothStatus = (toothNumber: number) => {
    if (missingTeeth.includes(toothNumber)) {
      // Move from Missing to Extracted
      setMissingTeeth((prev) => prev.filter((n) => n !== toothNumber));
      setExtractedTeeth((prev) => [...prev, toothNumber]);
    } else if (extractedTeeth.includes(toothNumber)) {
      // Move from Extracted to Present
      setExtractedTeeth((prev) => prev.filter((n) => n !== toothNumber));
    } else {
      // Move from Present to Missing
      setMissingTeeth((prev) => [...prev, toothNumber]);
    }
  };

  // Handle dental examination type change
  const handleDentalExaminationTypeChange = (type: "child" | "adult") => {
    setDentalExaminationType(type);

    // Clear selected teeth that are not in the new type's options
    const newUpperOptions = type === "child" ? [...CHILD_UPPER_ROW1, ...CHILD_UPPER_ROW2] : [...ADULT_UPPER_ROW1, ...ADULT_UPPER_ROW2];
    const newLowerOptions = type === "child" ? [...CHILD_LOWER_ROW1, ...CHILD_LOWER_ROW2] : [...ADULT_LOWER_ROW1, ...ADULT_LOWER_ROW2];
    const allNewOptions = [...newUpperOptions, ...newLowerOptions];

    setMissingTeeth(prev => prev.filter(tooth => allNewOptions.includes(tooth)));
    setExtractedTeeth(prev => prev.filter(tooth => allNewOptions.includes(tooth)));
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        medicalDropdownRef.current &&
        !medicalDropdownRef.current.contains(event.target as Node)
      ) {
        setIsMedicalDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (fetchedClinicsRef.current) return;
    fetchedClinicsRef.current = true;
    const fetchClinics = async () => {
      setClinicsLoading(true);
      try {
        const res = await getClinicsByUser();
        const list = res?.data?.data || res?.data || [];
        const normalized = (Array.isArray(list) ? list : [])
          .map((c) => {
            const id = c.id || c._id || c.clinicId || c.value || "";
            const name = c.name || c.clinicName || c.label || "";
            return { id, name };
          })
          .filter((c) => c.id && c.name);
        setClinics(normalized);
      } catch (err) {
        setClinics([]);
      } finally {
        setClinicsLoading(false);
      }
    };

    const fetchPatient = async () => {
      setPatientsLoading(true);
      try {
        const res = await getAllPatients();
        const list = res?.data?.data || res?.data || [];
        console.log(list);

        const normalized = (Array.isArray(list) ? list : [])
          .map((c) => {
            const id = c.id || c._id || c.patientId || c.value || "";
            const name = c.name || c.clinicName || c.label || "";
            const clinicId = c.clinicId || c.clinic?.id || "";
            const clinicName = c.clinic?.name || c.clinicName || "";
            return { id, name, clinicId, clinicName };
          })
          .filter((c) => c.id && c.name);
        setPatients(normalized);
      } catch (err) {
        setPatients([]);
      } finally {
        setPatientsLoading(false);
      }
    };
    fetchClinics();
    fetchPatient();
  }, []);

  // Handle query parameters for pre-filling form when coming from patient list
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const queryPatientId = searchParams.get('patientId');
    const queryClinicId = searchParams.get('clinicId');

    if (queryPatientId && queryClinicId && !id) {
      // Only pre-fill if we're creating a new treatment (not editing)
      setFormData(prev => ({
        ...prev,
        patientId: queryPatientId,
        clinicId: queryClinicId
      }));

      // Clear any validation errors since we're pre-filling valid data
      setErrors({});

      // Mark that form was pre-filled from patient
      setIsPreFilledFromPatient(true);
    }
  }, [location.search, id]);

  const selectedPatient = patients.find((p) => p.id === formData.patientId);
  // Filter patients so they depend on selected clinic
  const filteredPatients = formData.clinicId
    ? patients.filter((p) => p.clinicId === formData.clinicId)
    : patients;

  // Prefill when editing existing patient
  useEffect(() => {
    if (!id || fetchedPatientRef.current) return;
    fetchedPatientRef.current = true;
    const fetchPatient = async () => {
      setLoading(true);
      try {
        const res = await getTreatmentById(id as string);
        const data = res?.data?.data || res?.data;
        if (data) {
          setFormData((prev) => ({
            ...prev,
            id: data.id || data._id || (id as string),
            doctorId: data.doctorId || prev.doctorId,
            clinicId: data.clinic?.id || data.clinicId || prev.clinicId,
            patientId: data.patient?.id || data.patientId || prev.patientId,
            allergyId: data.allergyId || prev.allergyId,
            habit: data.habit || prev.habit,
            other_habit: data.other_habit || prev.other_habit,
            medicalConditionId: Array.isArray(data.medicalConditionId)
              ? data.medicalConditionId.join(",")
              : data.medicalConditionId || prev.medicalConditionId,
            chiefComplaint: data.chiefComplaint || prev.chiefComplaint,
            overjet: data.overjet || prev.overjet,
            diagnosis: data.diagnosis || prev.diagnosis,
            other_diagnosis: data.other_diagnosis || prev.other_diagnosis,
            facialProfile: data.facialProfile || prev.facialProfile,
            nasolabialAngle: data.nasolabialAngle || prev.nasolabialAngle,
            crossbite: data.crossbite || prev.crossbite,
            lipPosture: data.lipPosture || prev.lipPosture,
            noseSize: data.noseSize || prev.noseSize,
            treatmentType: data.treatmentType || prev.treatmentType,
            lipProtrusion: data.lipProtrusion || prev.lipProtrusion,
            vto: data.vto || prev.vto,
            tonsils: data.tonsils || prev.tonsils,
            facialForm: data.facialForm || prev.facialForm,
            mentolabialSulcus: data.mentolabialSulcus || prev.mentolabialSulcus,
            frenum: data.frenum || prev.frenum,
            oralHygieneStatus: data.oralHygieneStatus || prev.oralHygieneStatus,
            chin: data.chin || prev.chin,
            facialSymmetry: data.facialSymmetry || prev.facialSymmetry,
            facialDivergence: data.facialDivergence || prev.facialDivergence,
            tongueSize: data.tongueSize || prev.tongueSize,
            treatmentObjective:
              data.treatmentObjective || prev.treatmentObjective,
          }));
          setSelectedMedicalConditionIds(
            parseMedicalConditionIds(data.medicalConditionId)
          );

          // Load dental examination data
          if (data.jawType) {
            setDentalExaminationType(data.jawType as "child" | "adult");
          }
          if (data.dentalAndIntraOralExamination) {
            setMissingTeeth(data.dentalAndIntraOralExamination.missingTeeth || []);
            setExtractedTeeth(data.dentalAndIntraOralExamination.extractedTeeth || []);

            // Fallback for legacy data
            if (!data.dentalAndIntraOralExamination.missingTeeth && data.dentalAndIntraOralExamination.upperJawOptions) {
              setMissingTeeth([
                ...(data.dentalAndIntraOralExamination.upperJawOptions || []),
                ...(data.dentalAndIntraOralExamination.lowerJawOptions || [])
              ]);
            }
          }

                      // try to populate any existing photos array of strings or objects
          const existing = Array.isArray(data.preTreatmentPhotos)
            ? data.preTreatmentPhotos.map((p: any) => {
                if (typeof p === "string") return { id: p, url: p }; // Fallback
                return { id: p?.id || p?.url || "", url: p?.url || p?.name || "" };
              }).filter((v: { id: string; url: string }) => !!v.url)
            : [];
          setExistingPreTreatmentPhotos(existing);
        }
      } catch (err: any) {
        toast.error(err?.response?.data?.message || "Failed to fetch patient");
      } finally {
        setLoading(false);
      }
    };
    fetchPatient();
  }, [id]);

  useEffect(() => {
    const fetchMedicalConditions = async () => {
      setMedicalLoading(true);
      try {
        const res = await getAllMedicalConditions();
        const list = res?.data?.data || res?.data || [];
        const normalized = (Array.isArray(list) ? list : [])
          .map((c) => {
            const id = c.id || c._id || c.value || "";
            const name = c.name || c.label || "";
            return { id, name };
          })
          .filter((c) => c.id && c.name);
        setMedicalConditions(normalized);
      } catch {
        setMedicalConditions([]);
      } finally {
        setMedicalLoading(false);
      }
    };

    const fetchAllergies = async () => {
      setAllergyLoading(true);
      try {
        const res = await getAllAllergies();
        const list = res?.data?.data || res?.data || [];
        const normalized = (Array.isArray(list) ? list : [])
          .map((c) => {
            const id = c.id || c._id || c.value || "";
            const name = c.name || c.label || "";
            return { id, name };
          })
          .filter((c) => c.id && c.name);
        setAllergies(normalized);
      } catch {
        setAllergies([]);
      } finally {
        setAllergyLoading(false);
      }
    };
    fetchMedicalConditions();
    fetchAllergies();
  }, []);

  // Fetch all habits
  const fetchHabits = async () => {
    setHabitLoading(true);
    try {
      const res = await getAllHabits();
      const list = res?.data?.data || res?.data || [];
      const normalized = (Array.isArray(list) ? list : [])
        .map((c) => {
          const id = c.id || c._id || c.value || "";
          const name = c.name || c.label || "";
          return { id, name };
        })
        .filter((c) => c.id && c.name);
      setHabits(normalized);
    } catch (err) {
      setHabits([]);
    } finally {
      setHabitLoading(false);
    }
  };
  useEffect(() => {
    fetchHabits();
  }, []);
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    // Prevent changes to clinic and patient when pre-filled from patient list
    if (isPreFilledFromPatient && (name === "clinicId" || name === "patientId")) {
      return;
    }

    // Clear field-level error when user selects a value
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const toggleMedicalCondition = (conditionId: string) => {
    setSelectedMedicalConditionIds((prev) => {
      const exists = prev.includes(conditionId);
      const next = exists
        ? prev.filter((id) => id !== conditionId)
        : [...prev, conditionId];
      setFormData((prevFormData) => ({
        ...prevFormData,
        medicalConditionId: next.join(","),
      }));
      return next;
    });
  };

  const validate = () => {
    const nextErrors: typeof errors = {};
    if (!formData.patientId) nextErrors.patientId = "Patient is required";
    if (!formData.clinicId) nextErrors.clinicId = "Clinic is required";
    // Enum/select fields validation
    const requiredSelects: Array<{
      key: keyof typeof errors;
      value: string;
      label: string;
    }> = [
        { key: "facialDivergence", value: formData.facialDivergence, label: "Facial Divergence" },
        { key: "tongueSize", value: formData.tongueSize, label: "Tongue Size" },
        { key: "facialSymmetry", value: formData.facialSymmetry, label: "Facial Symmetry" },
        { key: "chin", value: formData.chin, label: "Chin" },
        { key: "oralHygieneStatus", value: formData.oralHygieneStatus, label: "Oral Hygiene" },
        { key: "frenum", value: formData.frenum, label: "Frenum" },
        { key: "mentolabialSulcus", value: formData.mentolabialSulcus, label: "Mentolabial Sulcus" },
        { key: "facialForm", value: formData.facialForm, label: "Facial Form" },
        { key: "tonsils", value: formData.tonsils, label: "Tonsils" },
        { key: "vto", value: formData.vto, label: "VTO" },
        { key: "lipProtrusion", value: formData.lipProtrusion, label: "Lip Protrusion" },
        { key: "treatmentType", value: formData.treatmentType, label: "Treatment Type" },
        { key: "noseSize", value: formData.noseSize, label: "Nose Size" },
        { key: "lipPosture", value: formData.lipPosture, label: "Lip Posture" },
        { key: "crossbite", value: formData.crossbite, label: "Crossbite" },
        { key: "nasolabialAngle", value: formData.nasolabialAngle, label: "Nasolabial Angle" },
        { key: "facialProfile", value: formData.facialProfile, label: "Facial Profile" },
      ];

    requiredSelects.forEach(({ key, value, label }) => {
      if (!value) {
        nextErrors[key] = `${label} is required`;
      }
    });

    // Validate treatmentObjective length
    if (formData.treatmentObjective && formData.treatmentObjective.length > 500) {
      nextErrors.treatmentObjective = "Treatment Objective must be 500 characters or less";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;

    const totalImages = existingPreTreatmentPhotos.length + preTreatmentPhotos.length + files.length;

    // Check if adding these files would exceed the 10 image limit
    if (totalImages > 10) {
      const remainingSlots = 10 - existingPreTreatmentPhotos.length - preTreatmentPhotos.length;
      toast.error(`Maximum 10 images allowed. You can add ${remainingSlots} more image(s).`);
      e.currentTarget.value = "";
      return;
    }

    // append to existing, de-duplicate by name+size+lastModified
    setPreTreatmentPhotos((prev) => {
      const merged = [...prev, ...files];
      const seen = new Set<string>();
      const unique: File[] = [];
      for (const f of merged) {
        const key = `${f.name}-${f.size}-${f.lastModified}`;
        if (!seen.has(key)) {
          seen.add(key);
          unique.push(f);
        }
      }
      return unique;
    });
    // allow selecting the same file again later by resetting input value
    e.currentTarget.value = "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const payload: CreateUpdateTreatmentPayload = {
        ...formData,
        jawType: dentalExaminationType,
        dentalAndIntraOralExamination: {
          missingTeeth,
          extractedTeeth,
        },
      };

      await createOrUpdateTreatment(payload, preTreatmentPhotos);
      toast.success("Treatment saved successfully");
      navigate(`/treatments`);
      setFormData({
        id: "",
        patientId: "",
        doctorId: "",
        clinicId: "",
        medicalConditionId: "",
        overjet: "",
        allergyId: "",
        habit: "",
        other_habit: "",
        diagnosis: "",
        other_diagnosis: "",
        chiefComplaint: "",
        facialProfile: "",
        nasolabialAngle: "",
        crossbite: "",
        lipPosture: "",
        noseSize: "",
        treatmentType: "",
        lipProtrusion: "",
        vto: "",
        tonsils: "",
        facialForm: "",
        mentolabialSulcus: "",
        frenum: "",
        oralHygieneStatus: "",
        chin: "",
        facialSymmetry: "",
        facialDivergence: "",
        tongueSize: "",
        treatmentObjective: "",
      });
      setPreTreatmentPhotos([]);
      setExistingPreTreatmentPhotos([]);
      setSelectedMedicalConditionIds([]);
      setIsMedicalDropdownOpen(false);
      setMissingTeeth([]);
      setExtractedTeeth([]);
      setDentalExaminationType("adult");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to save patient");
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePhoto = (indexToRemove: number) => {
    setPreTreatmentPhotos((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    // Clear treatmentObjective error when user types
    if (name === "treatmentObjective" && errors.treatmentObjective) {
      setErrors((prev) => ({ ...prev, treatmentObjective: undefined }));
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDeleteExistingPhoto = async (photoId: string) => {
    try {
      setLoading(true);
      await deletePreTreatmentPhoto(photoId);
      toast.success("Pre-treatment photo deleted successfully");
      setExistingPreTreatmentPhotos((prev) =>
        prev.filter((item) => item.id !== photoId)
      );
      setShowPhotoDeleteConfirm(false);
      setSelectedPhotoId("");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to delete photo.");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <Loader />} {patientsLoading && <Loader />}{" "}
      {clinicsLoading && <Loader />}
      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />
      <div className="flex-1 flex flex-col">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

        <main className="p-4 md:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-3xl shadow-2xl bg-white p-8"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                  {id ? "Edit Treatment" : "Create Treatment"}
                </h2>
                <button
                  className="px-4 py-1 rounded-xl bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200 transition flex items-center gap-2"
                  onClick={() => window.history.back()}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-4 h-4"
                    aria-hidden="true"
                  >
                    <path d="M10 19l-7-7 7-7" />
                    <path d="M3 12h18" />
                  </svg>
                  Back
                </button>
              </div>

              {/* Info message when pre-filled from patient list */}
              {isPreFilledFromPatient && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center">
                    <Lock className="w-4 h-4 text-blue-600 mr-2" />
                    <p className="text-sm text-blue-800">
                      <strong>Pre-filled from Patient:</strong> Clinic and Patient fields are locked and cannot be changed.
                      Please fill in the remaining fields to complete the treatment.
                    </p>
                  </div>
                </div>
              )}

              <form
                noValidate
                onSubmit={handleSubmit}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                {/* Clinic */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Clinic <span className="text-red-500">*</span>
                    {isPreFilledFromPatient && (
                      <span className="ml-2 text-xs text-blue-600 font-normal inline-flex items-center">
                        <Lock className="w-3 h-3 mr-1" />
                        (Pre-filled from patient)
                      </span>
                    )}
                  </label>
                  <select
                    name="clinicId"
                    value={formData.clinicId}
                    onChange={handleChange}
                    required
                    disabled={clinicsLoading || isPreFilledFromPatient}
                    className={`w-full mt-1 p-2 border rounded-lg ${isPreFilledFromPatient
                      ? "bg-gray-100 cursor-not-allowed"
                      : ""
                      } ${errors.clinicId
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : ""
                      }`}
                  >
                    {clinicsLoading ? (
                      <option value="">Loading clinics...</option>
                    ) : (
                      <>
                        <option value="" disabled>
                          Select Clinic
                        </option>
                        {clinics.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                  {errors.clinicId && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.clinicId}
                    </p>
                  )}
                </div>

                {/* Patient (filtered by selected clinic) */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Patient <span className="text-red-500">*</span>
                    {isPreFilledFromPatient && (
                      <span className="ml-2 text-xs text-blue-600 font-normal inline-flex items-center">
                        <Lock className="w-3 h-3 mr-1" />
                        (Pre-filled from patient)
                      </span>
                    )}
                  </label>
                  <select
                    name="patientId"
                    value={formData.patientId}
                    onChange={handleChange}
                    required
                    disabled={patientsLoading || !formData.clinicId || isPreFilledFromPatient}
                    className={`w-full mt-1 p-2 border rounded-lg ${isPreFilledFromPatient
                      ? "bg-gray-100 cursor-not-allowed"
                      : ""
                      } ${errors.patientId
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : ""
                      }`}
                  >
                    {patientsLoading ? (
                      <option value="">Loading Patients...</option>
                    ) : !formData.clinicId ? (
                      <option value="">Select clinic first</option>
                    ) : filteredPatients.length === 0 ? (
                      <option value="" disabled>
                        Please add patient in clinic..
                      </option>
                    ) : (
                      <>
                        <option value="" disabled>
                          Select Patient
                        </option>
                        {filteredPatients.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                  {errors.patientId && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.patientId}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Medical Condition
                  </label>
                  <div className="mt-1 relative" ref={medicalDropdownRef}>
                    <button
                      type="button"
                      onClick={() =>
                        !medicalLoading &&
                        setIsMedicalDropdownOpen((prev) => !prev)
                      }
                      disabled={medicalLoading}
                      className={`w-full flex flex-wrap gap-2 items-center justify-between border rounded-xl px-3 py-2 text-left ${medicalLoading ? "bg-gray-50 cursor-not-allowed" : ""
                        }`}
                    >
                      <div className="flex flex-wrap gap-2">
                        {selectedMedicalConditionIds.length ? (
                          selectedMedicalConditionIds.map((conditionId) => {
                            const condition = medicalConditions.find(
                              (item) => item.id === conditionId
                            );
                            const label = condition?.name || conditionId;
                            return (
                              <span
                                key={conditionId}
                                className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full"
                              >
                                {label}
                              </span>
                            );
                          })
                        ) : (
                          <span className="text-gray-500 text-sm">
                            Select medical conditions
                          </span>
                        )}
                      </div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`w-4 h-4 text-gray-500 transition-transform ${isMedicalDropdownOpen ? "rotate-180" : ""
                          }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {isMedicalDropdownOpen && (
                      <div className="absolute z-20 mt-2 w-full bg-white border rounded-xl shadow-lg max-h-60 overflow-y-auto">
                        {medicalLoading ? (
                          <p className="px-3 py-2 text-sm text-gray-500">
                            Loading conditions...
                          </p>
                        ) : medicalConditions.length === 0 ? (
                          <p className="px-3 py-2 text-sm text-yellow-600">
                            No medical conditions available.
                          </p>
                        ) : (
                          medicalConditions.map((condition) => {
                            const selected = selectedMedicalConditionIds.includes(
                              condition.id
                            );
                            return (
                              <label
                                key={condition.id}
                                className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                              >
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  onChange={() =>
                                    toggleMedicalCondition(condition.id)
                                  }
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className="text-gray-700">
                                  {condition.name}
                                </span>
                              </label>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Allergy
                  </label>
                  <select
                    name="allergyId"
                    value={formData.allergyId}
                    onChange={handleChange}
                    disabled={allergyLoading}
                    className="w-full mt-1 p-2 border rounded-lg"
                  >
                    {allergyLoading ? (
                      <option value="">Loading allergies...</option>
                    ) : (
                      <>
                        <option value="" disabled>
                          Select Allergy
                        </option>
                        {allergies.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Habits
                  </label>
                  <select
                    name="habit"
                    value={formData.habit}
                    onChange={(e) => {
                      handleChange(e);

                      if (e.target.value !== "Other") {
                        setFormData((prev) => ({
                          ...prev,
                          other_habit: "",
                        }));
                      }
                    }}
                    disabled={habitLoading}
                    className="w-full mt-1 p-2 border rounded-lg"
                  >
                    {habitLoading ? (
                      <option value="">Loading habits...</option>
                    ) : (
                      <>
                        <option value="" disabled>
                          Select Habit
                        </option>
                        {habits.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                        <option value="Other">Other</option>
                      </>
                    )}
                  </select>

                  {formData.habit === "Other" && (
                    <input
                      type="text"
                      name="other_habit"
                      value={formData.other_habit}
                      onChange={handleInputChange}
                      placeholder="Enter other habit"
                      className="mt-2 w-full p-2 border rounded-xl"
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Diagnosis
                  </label>

                  <select
                    name="diagnosis"
                    value={formData.diagnosis}
                    onChange={(e) => {
                      handleChange(e);

                      if (e.target.value !== "Other") {
                        setFormData((prev) => ({
                          ...prev,
                          other_diagnosis: "",
                        }));
                      }
                    }}
                    className="mt-1 w-full p-2 border rounded-xl"
                  >
                    <option value="" disabled>
                      Select Diagnosis
                    </option>
                    {diagnosis_options.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>

                  {formData.diagnosis === "Other" && (
                    <input
                      type="text"
                      name="other_diagnosis"
                      value={formData.other_diagnosis}
                      onChange={handleInputChange}
                      placeholder="Enter other diagnosis"
                      className="mt-2 w-full p-2 border rounded-xl"
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Chief Complaint
                  </label>
                  <input
                    name="chiefComplaint"
                    value={formData.chiefComplaint}
                    onChange={handleChange}
                    className="mt-1 w-full p-2 border rounded-xl"
                  />
                </div>

                {/* <div className="relative w-full">
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Diagnosis
                  </label>

                  <div
                    className="w-full p-2 border rounded-xl cursor-pointer bg-white"
                    onClick={() => setOpen(!open)}
                  >
                    {formData.diagnosis || "Select Diagnosis"}
                  </div>

                  {open && (
                    <div className="absolute z-10 w-full bg-white border rounded-xl mt-1">
                      {diagnosis_options.map((option) => (
                        <div key={option}>
                          <div
                            className="px-3 py-2 hover:bg-blue cursor-pointer"
                            onClick={() => {
                              setFormData((prev) => ({
                                ...prev,
                                diagnosis: option,
                              }));
                              setOpen(false);
                            }}
                          >
                            {option}
                          </div>

                          {option === "Other" && formData.diagnosis === "Other" && (
                            <div className="px-3 pb-2">
                              <input
                                type="text"
                                placeholder="Enter other diagnosis"
                                value={formData.other_diagnosis}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    other_diagnosis: e.target.value,
                                  }))
                                }
                                className="w-full p-2 border rounded-lg"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div> */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Overjet
                  </label>
                  <input
                    name="overjet"
                    value={formData.overjet}
                    onChange={handleChange}
                    className="mt-1 w-full p-2 border rounded-xl"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Facial Divergence
                  </label>
                  <select
                    name="facialDivergence"
                    value={formData.facialDivergence}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.facialDivergence ? "border-red-500 focus:ring-red-500 focus:border-red-500" : ""}`}
                    required
                  >
                    <option value="" disabled>
                      Select Facial Divergence
                    </option>
                    <option value="STRAIGHT">Straight</option>
                    <option value="ANTERIOR">Anterior</option>
                    <option value="POSTERIOR">Posterior</option>
                  </select>
                  {errors.facialDivergence && (
                    <p className="text-red-500 text-sm mt-1">{errors.facialDivergence}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Tongue Size
                  </label>
                  <select
                    name="tongueSize"
                    value={formData.tongueSize}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.tongueSize ? "border-red-500 focus:ring-red-500 focus:border-red-500" : ""}`}
                    required
                  >
                    <option value="" disabled>
                      Select Tongue Size
                    </option>
                    <option value="SMALL">Small</option>
                    <option value="NORMAL">Normal</option>
                    <option value="LARGE">Large</option>
                  </select>
                  {errors.tongueSize && (
                    <p className="text-red-500 text-sm mt-1">{errors.tongueSize}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Facial Symmetry
                  </label>
                  <select
                    name="facialSymmetry"
                    value={formData.facialSymmetry}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.facialSymmetry ? "border-red-500 focus:ring-red-500 focus:border-red-500" : ""}`}
                    required
                  >
                    <option value="" disabled>
                      Select Facial Symmetry
                    </option>
                    <option value="SYMMETRIC">Symmetric</option>
                    <option value="ASYMMETRIC">Asymmetric</option>
                  </select>
                  {errors.facialSymmetry && (
                    <p className="text-red-500 text-sm mt-1">{errors.facialSymmetry}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Chin
                  </label>
                  <select
                    name="chin"
                    value={formData.chin}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.chin ? "border-red-500 focus:ring-red-500 focus:border-red-500" : ""}`}
                    required
                  >
                    <option value="" disabled>
                      Select Chin
                    </option>
                    <option value="NORMAL">Normal</option>
                    <option value="DEFICIENT">Deficient</option>
                    <option value="PROMINENT">Prominent</option>
                  </select>
                  {errors.chin && (
                    <p className="text-red-500 text-sm mt-1">{errors.chin}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Oral Hygiene
                  </label>
                  <select
                    name="oralHygieneStatus"
                    value={formData.oralHygieneStatus}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.oralHygieneStatus ? "border-red-500 focus:ring-red-500 focus:border-red-500" : ""}`}
                    required
                  >
                    <option value="" disabled>
                      Select Oral Hygiene
                    </option>
                    <option value="GOOD">Good</option>
                    <option value="AVERAGE">Average</option>
                    <option value="POOR">Poor</option>
                  </select>
                  {errors.oralHygieneStatus && (
                    <p className="text-red-500 text-sm mt-1">{errors.oralHygieneStatus}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Frenum
                  </label>
                  <select
                    name="frenum"
                    value={formData.frenum}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.frenum ? "border-red-500 focus:ring-red-500 focus:border-red-500" : ""}`}
                    required
                  >
                    <option value="" disabled>
                      Select Frenum
                    </option>
                    <option value="NORMAL">Normal</option>
                    <option value="HIGH">High</option>
                  </select>
                  {errors.frenum && (
                    <p className="text-red-500 text-sm mt-1">{errors.frenum}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Mentolabial Sulcus
                  </label>
                  <select
                    name="mentolabialSulcus"
                    value={formData.mentolabialSulcus}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.mentolabialSulcus ? "border-red-500 focus:ring-red-500 focus:border-red-500" : ""}`}
                    required
                  >
                    <option value="" disabled>
                      Select Mentolabial Sulcus
                    </option>
                    <option value="SHALLOW">Shallow</option>
                    <option value="DEEP">Deep</option>
                  </select>
                  {errors.mentolabialSulcus && (
                    <p className="text-red-500 text-sm mt-1">{errors.mentolabialSulcus}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Facial Form
                  </label>
                  <select
                    name="facialForm"
                    value={formData.facialForm}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.facialForm ? "border-red-500 focus:ring-red-500 focus:border-red-500" : ""}`}
                    required
                  >
                    <option value="" disabled>
                      Select Facial Form
                    </option>
                    <option value="MESO">Meso</option>
                    <option value="LEPTO">Lepto</option>
                    <option value="EURY">Eury</option>
                  </select>
                  {errors.facialForm && (
                    <p className="text-red-500 text-sm mt-1">{errors.facialForm}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Tonsils
                  </label>
                  <select
                    name="tonsils"
                    value={formData.tonsils}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.tonsils ? "border-red-500 focus:ring-red-500 focus:border-red-500" : ""}`}
                    required
                  >
                    <option value="" disabled>
                      Select Tonsils
                    </option>
                    <option value="NORMAL">Normal</option>
                    <option value="HYPERTROPHIED">Hypertrophied</option>
                  </select>
                  {errors.tonsils && (
                    <p className="text-red-500 text-sm mt-1">{errors.tonsils}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    VTO
                  </label>
                  <select
                    name="vto"
                    value={formData.vto}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.vto ? "border-red-500 focus:ring-red-500 focus:border-red-500" : ""}`}
                    required
                  >
                    <option value="" disabled>
                      Select VTO
                    </option>
                    <option value="POSITIVE">Positive</option>
                    <option value="NEGATIVE">Negative</option>
                  </select>
                  {errors.vto && (
                    <p className="text-red-500 text-sm mt-1">{errors.vto}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Lip Protrusion
                  </label>
                  <select
                    name="lipProtrusion"
                    value={formData.lipProtrusion}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.lipProtrusion ? "border-red-500 focus:ring-red-500 focus:border-red-500" : ""}`}
                    required
                  >
                    <option value="" disabled>
                      Select Lip Protrusion
                    </option>
                    <option value="PRESENT">Present</option>
                    <option value="ABSENT">Absent</option>
                  </select>
                  {errors.lipProtrusion && (
                    <p className="text-red-500 text-sm mt-1">{errors.lipProtrusion}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Treatment Type
                  </label>
                  <select
                    name="treatmentType"
                    value={formData.treatmentType}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.treatmentType ? "border-red-500 focus:ring-red-500 focus:border-red-500" : ""}`}
                    required
                  >
                    <option value="" disabled>
                      Select Treatment Type
                    </option>
                    <option value="EXTRACTION">Extraction</option>
                    <option value="NON_EXTRACTION">Non Extraction</option>
                  </select>
                  {errors.treatmentType && (
                    <p className="text-red-500 text-sm mt-1">{errors.treatmentType}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Nose Size
                  </label>
                  <select
                    name="noseSize"
                    value={formData.noseSize}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.noseSize ? "border-red-500 focus:ring-red-500 focus:border-red-500" : ""}`}
                    required
                  >
                    <option value="" disabled>
                      Select Nose Size
                    </option>
                    <option value="SMALL">Small</option>
                    <option value="NORMAL">Normal</option>
                    <option value="LARGE">Large</option>
                  </select>
                  {errors.noseSize && (
                    <p className="text-red-500 text-sm mt-1">{errors.noseSize}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Lip Posture
                  </label>
                  <select
                    name="lipPosture"
                    value={formData.lipPosture}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.lipPosture ? "border-red-500 focus:ring-red-500 focus:border-red-500" : ""}`}
                    required
                  >
                    <option value="" disabled>
                      Select Lip Posture
                    </option>
                    <option value="COMPETENT">Competent</option>
                    <option value="INCOMPETENT">Incompetent</option>
                  </select>
                  {errors.lipPosture && (
                    <p className="text-red-500 text-sm mt-1">{errors.lipPosture}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Crossbite
                  </label>
                  <select
                    name="crossbite"
                    value={formData.crossbite}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.crossbite ? "border-red-500 focus:ring-red-500 focus:border-red-500" : ""}`}
                    required
                  >
                    <option value="" disabled>
                      Select Crossbite
                    </option>
                    <option value="PRESENT">Present</option>
                    <option value="ABSENT">Absent</option>
                  </select>
                  {errors.crossbite && (
                    <p className="text-red-500 text-sm mt-1">{errors.crossbite}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Nasolabial Angle
                  </label>
                  <select
                    name="nasolabialAngle"
                    value={formData.nasolabialAngle}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.nasolabialAngle ? "border-red-500 focus:ring-red-500 focus:border-red-500" : ""}`}
                    required
                  >
                    <option value="" disabled>
                      Select Nasolabial Angle
                    </option>
                    <option value="NORMAL">Normal</option>
                    <option value="ACUTE">Acute</option>
                    <option value="OBTUSE">Obtuse</option>
                  </select>
                  {errors.nasolabialAngle && (
                    <p className="text-red-500 text-sm mt-1">{errors.nasolabialAngle}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Facial Profile
                  </label>
                  <select
                    name="facialProfile"
                    value={formData.facialProfile}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.facialProfile ? "border-red-500 focus:ring-red-500 focus:border-red-500" : ""}`}
                    required
                  >
                    <option value="" disabled>
                      Select Facial Profile
                    </option>
                    <option value="NORMAL">Normal</option>
                    <option value="CONVEX">Convex</option>
                    <option value="CONCAVE">Concave</option>
                  </select>
                  {errors.facialProfile && (
                    <p className="text-red-500 text-sm mt-1">{errors.facialProfile}</p>
                  )}
                </div>

                {/* Dental & Intra Oral Examination */}
                <div className="md:col-span-2 space-y-6 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                      Dental & Intra Oral Examination
                    </h3>
                    <div className="flex bg-gray-100 p-1 rounded-xl">
                      {DENTAL_EXAMINATION_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => handleDentalExaminationTypeChange(option.value as "child" | "adult")}
                          className={`px-6 py-1.5 rounded-lg text-sm font-medium transition-all ${dentalExaminationType === option.value
                            ? "bg-white text-blue-600 shadow-sm"
                            : "text-gray-500 hover:text-gray-700"
                            }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Upper Jaw Grid */}
                    <div className="space-y-4">
                      <label className="block text-sm font-semibold text-gray-700">Upper jaw</label>
                      <div className="flex flex-col gap-3 overflow-x-auto pb-2">
                        <div className="flex gap-2 min-w-max">
                          {(dentalExaminationType === "adult" ? ADULT_UPPER_ROW1 : CHILD_UPPER_ROW1).map((num) => {
                            const status = missingTeeth.includes(num) ? "missing" : extractedTeeth.includes(num) ? "extracted" : "present";
                            return (
                              <button
                                key={num}
                                type="button"
                                onClick={() => cycleToothStatus(num)}
                                className={`w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg border text-sm font-bold transition-all shadow-sm ${status === "missing"
                                  ? "bg-red-200 border-red-300 text-red-700"
                                  : status === "extracted"
                                    ? "bg-gray-200 border-gray-300 text-gray-700"
                                    : "bg-cyan-100 border-cyan-200 text-cyan-700"
                                  }`}
                              >
                                {num}
                              </button>
                            );
                          })}
                        </div>
                        <div className="flex gap-2 min-w-max">
                          {(dentalExaminationType === "adult" ? ADULT_UPPER_ROW2 : CHILD_UPPER_ROW2).map((num) => {
                            const status = missingTeeth.includes(num) ? "missing" : extractedTeeth.includes(num) ? "extracted" : "present";
                            return (
                              <button
                                key={num}
                                type="button"
                                onClick={() => cycleToothStatus(num)}
                                className={`w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg border text-sm font-bold transition-all shadow-sm ${status === "missing"
                                  ? "bg-red-200 border-red-300 text-red-700"
                                  : status === "extracted"
                                    ? "bg-gray-200 border-gray-300 text-gray-700"
                                    : "bg-cyan-100 border-cyan-200 text-cyan-700"
                                  }`}
                              >
                                {num}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Lower Jaw Grid */}
                    <div className="space-y-4">
                      <label className="block text-sm font-semibold text-gray-700">Lower jaw</label>
                      <div className="flex flex-col gap-3 overflow-x-auto pb-2">
                        <div className="flex gap-2 min-w-max">
                          {(dentalExaminationType === "adult" ? ADULT_LOWER_ROW1 : CHILD_LOWER_ROW1).map((num) => {
                            const status = missingTeeth.includes(num) ? "missing" : extractedTeeth.includes(num) ? "extracted" : "present";
                            return (
                              <button
                                key={num}
                                type="button"
                                onClick={() => cycleToothStatus(num)}
                                className={`w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg border text-sm font-bold transition-all shadow-sm ${status === "missing"
                                  ? "bg-red-200 border-red-300 text-red-700"
                                  : status === "extracted"
                                    ? "bg-gray-200 border-gray-300 text-gray-700"
                                    : "bg-cyan-100 border-cyan-200 text-cyan-700"
                                  }`}
                              >
                                {num}
                              </button>
                            );
                          })}
                        </div>
                        <div className="flex gap-2 min-w-max">
                          {(dentalExaminationType === "adult" ? ADULT_LOWER_ROW2 : CHILD_LOWER_ROW2).map((num) => {
                            const status = missingTeeth.includes(num) ? "missing" : extractedTeeth.includes(num) ? "extracted" : "present";
                            return (
                              <button
                                key={num}
                                type="button"
                                onClick={() => cycleToothStatus(num)}
                                className={`w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg border text-sm font-bold transition-all shadow-sm ${status === "missing"
                                  ? "bg-red-200 border-red-300 text-red-700"
                                  : status === "extracted"
                                    ? "bg-gray-200 border-gray-300 text-gray-700"
                                    : "bg-cyan-100 border-cyan-200 text-cyan-700"
                                  }`}
                              >
                                {num}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Legend and Summary */}
                  <div className="pt-4 border-t border-gray-100">
                    <div className="flex flex-wrap gap-6 mb-4">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-cyan-100 border border-cyan-200"></div>
                        <span className="text-sm text-gray-600">Present</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-red-200 border border-red-300"></div>
                        <span className="text-sm text-gray-600">Missing</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-gray-200 border border-gray-300"></div>
                        <span className="text-sm text-gray-600">Extracted</span>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm text-gray-700">
                      <p><span className="font-semibold">Missing :</span> {missingTeeth.length > 0 ? missingTeeth.sort((a, b) => a - b).join(", ") : "None"}</p>
                      <p><span className="font-semibold">Extracted :</span> {extractedTeeth.length > 0 ? extractedTeeth.sort((a, b) => a - b).join(", ") : "None"}</p>
                    </div>
                  </div>
                </div>

                {/* treatmentObjective */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Treatment Objective
                  </label>
                  <textarea
                    name="treatmentObjective"
                    value={formData.treatmentObjective}
                    onChange={handleInputChange}
                    rows={3}
                    maxLength={500}
                    className={`w-full p-3 border rounded-xl transition-all duration-200 resize-none ${errors.treatmentObjective
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : ""
                      }`}
                    placeholder="Enter treatment objective here... (max 500 characters)"
                  />
                  <div className="flex justify-between items-center">
                    <div>
                      {errors.treatmentObjective && (
                        <p className="text-red-500 text-sm">{errors.treatmentObjective}</p>
                      )}
                    </div>
                    <div className={`text-sm ${formData.treatmentObjective.length > 400
                      ? formData.treatmentObjective.length > 480
                        ? "text-red-500 font-medium"
                        : "text-orange-500"
                      : "text-gray-500"
                      }`}>
                      {formData.treatmentObjective.length}/500 characters
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Pre-treatment Images - Max 10 images
                  </label>
                  <div className="mt-2 flex items-center gap-3">
                    <input
                      type="file"
                      id="preTreatmentPhotos"
                      accept="image/*"
                      multiple
                      onChange={handleFileChange}
                      className="hidden"
                    />

                    <label
                      htmlFor="preTreatmentPhotos"
                      className={`px-2 py-1 rounded-lg text-white cursor-pointer transition ${existingPreTreatmentPhotos.length + preTreatmentPhotos.length >= 10
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                        }`}
                    >
                      Upload Files
                    </label>

                    {/* show file count if selected */}
                    {(existingPreTreatmentPhotos.length + preTreatmentPhotos.length) > 0 && (
                      <span className={`text-sm ${existingPreTreatmentPhotos.length + preTreatmentPhotos.length >= 10
                        ? "text-red-600 font-medium"
                        : "text-gray-600"
                        }`}>
                        {existingPreTreatmentPhotos.length + preTreatmentPhotos.length}/10 images
                      </span>
                    )}

                    {(preTreatmentPhotos.length > 0 ||
                      existingPreTreatmentPhotos.length > 0) && (
                        <button
                          type="button"
                          onClick={() => setIsPhotosModalOpen(true)}
                          className="px-3 py-1 rounded-lg bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200 transition"
                        >
                          View images
                        </button>
                      )}
                  </div>
                </div>

                {isPhotosModalOpen && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div
                      className="absolute inset-0 bg-black/40"
                      onClick={() => setIsPhotosModalOpen(false)}
                    />
                    <div className="relative z-10 w-full max-w-lg bg-white rounded-2xl shadow-2xl p-5">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold text-gray-800">
                          Pre-treatment Images ({existingPreTreatmentPhotos.length + preTreatmentPhotos.length}/10)
                        </h3>
                        <button
                          type="button"
                          onClick={() => setIsPhotosModalOpen(false)}
                          className="text-gray-500 hover:text-gray-700"
                          aria-label="Close"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="w-5 h-5"
                          >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      </div>

                      {existingPreTreatmentPhotos.length > 0 && (
                        <div className="mb-4">
                          <div className="text-xs font-medium text-gray-500 mb-2">
                            Existing ({existingPreTreatmentPhotos.length})
                          </div>
                          <ul className="space-y-2 max-h-40 overflow-auto pr-1">
                            {/* {existingPreTreatmentPhotos.map((item, idx) => (
                              <li
                                key={`existing-${idx}`}
                                className="flex items-center justify-between gap-3 border border-gray-200 rounded-lg px-3 py-2"
                              >
                                <a
                                  href={item.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-sm text-blue-600 hover:underline"
                                  title={item.url}
                                >
                                  Image {idx + 1}
                                </a>
                              </li>
                            ))} */}
                            {existingPreTreatmentPhotos.map((item, idx) => (
                              <li
                                key={`existing-${idx}`}
                                className="flex items-center justify-between gap-3 border border-gray-200 rounded-lg px-3 py-2"
                              >
                                <a
                                  href={item.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-sm text-blue-600 hover:underline"
                                  title={item.url}
                                >
                                  Image {idx + 1}
                                </a>

                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedPhotoId(item.id);
                                    setShowPhotoDeleteConfirm(true);
                                  }}
                                  className="text-gray-500 hover:text-red-600 text-sm"
                                >
                                  Remove
                                </button>
                              </li>
                            ))}

                          </ul>
                        </div>
                      )}

                      <div>
                        <div className="text-xs font-medium text-gray-500 mb-2">
                          Selected to upload ({preTreatmentPhotos.length})
                        </div>
                        {preTreatmentPhotos.length === 0 ? (
                          <div className="text-sm text-gray-500">
                            No new files selected.
                          </div>
                        ) : (
                          <ul className="space-y-2 max-h-52 overflow-auto pr-1">
                            {preTreatmentPhotos.map((file, index) => (
                              <li
                                key={`${file.name}-${index}`}
                                className="flex items-center justify-between gap-3 border border-gray-200 rounded-lg px-3 py-2"
                              >
                                <span
                                  className="text-sm text-gray-800"
                                  title={file.name}
                                >
                                  File {existingPreTreatmentPhotos.length + index + 1} ({file.name})
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleRemovePhoto(index)}
                                  className="text-gray-500 hover:text-red-600"
                                  aria-label="Remove"
                                >
                                  Remove
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      <div className="mt-5 flex justify-end">
                        <button
                          type="button"
                          onClick={() => setIsPhotosModalOpen(false)}
                          className="px-4 py-2 rounded-xl bg-green-600 text-white hover:bg-green-700 transition"
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="md:col-span-2 flex justify-end gap-3 mt-4">
                  <button
                    type="button"
                    className="px-5 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                    onClick={() => window.history.back()}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-5 py-2 rounded-xl   text-white  transition disabled:opacity-60"
                  >
                    {loading ? "Saving..." : "Save Treatment"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        </main>
      </div>

      <ConfirmModal
        isOpen={showPhotoDeleteConfirm}
        title="Delete Pre-Treatment Photo"
        message="Are you sure you want to delete this photo?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={() => handleDeleteExistingPhoto(selectedPhotoId)}
        onCancel={() => {
          setShowPhotoDeleteConfirm(false);
          setSelectedPhotoId("");
        }}
        loading={loading}
        zIndex={100}
      />
    </div>
  );
};

export default PatientCreate;
